// src/services/pickerCostCheck.ts
// -------------------------------
// Sanity check on the ACTUAL cost a picker types before pressing Completed
// (David, 25 Oct 2026). Two typos reached prod and Unleashed: 2026-03269 Child 1
// Stationery $551,760.00 (meant $55.17) and 2026-03270 Child 4 Stationery
// $10,756,120.00 (meant $118.32). The cost box only required > 0 and != estimate.
//
// An amount is flagged when it is over the ceiling for its kind, OR more than
// PICKER_COST_MAX_RATIO times its estimate (only when there is an estimate > 0).
// A flag never blocks: the picker is asked to confirm, and may go ahead.
// Picker screen only — the caseworker wizard (default amounts set by Zephyr,
// reviewed by responders) and the top-up picker (one total per stock order, no
// per-item estimate) are deliberately NOT checked (David, 25 Oct 2026).

export type PickerItemKind = "device" | "fee";

export const PICKER_COST_CEILING: Record<PickerItemKind, number> = {
  device: 1500,
  fee: 500, // a Stationery Pack
};

export const PICKER_COST_MAX_RATIO = 3;

const KIND_NAME: Record<PickerItemKind, string> = {
  device: "a device",
  fee: "a Stationery Pack",
};

export interface PickerCostInput {
  label: string;
  kind: PickerItemKind;
  amount: number | string | null | undefined;
  estimate: number | string | null | undefined;
}

export function formatMoney(n: number): string {
  return `$${n.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// One line describing why this amount looks wrong, or null when it looks fine.
export function costWarning(item: PickerCostInput): string | null {
  const amt = Number(item.amount);
  if (!Number.isFinite(amt) || amt <= 0) return null;
  const est = Number(item.estimate);
  const hasEst = Number.isFinite(est) && est > 0;
  const ceiling = PICKER_COST_CEILING[item.kind];
  const reasons: string[] = [];
  if (amt > ceiling)
    reasons.push(`over the ${formatMoney(ceiling)} limit for ${KIND_NAME[item.kind]}`);
  if (hasEst && amt > est * PICKER_COST_MAX_RATIO) {
    reasons.push(`more than ${PICKER_COST_MAX_RATIO} times the estimate`);
  }
  if (reasons.length === 0) return null;
  const estText = hasEst ? ` (estimate ${formatMoney(est)})` : "";
  return `${item.label}: ${formatMoney(amt)}${estText} is ${reasons.join(" and ")}.`;
}

export function costWarnings(items: PickerCostInput[]): string[] {
  const out: string[] = [];
  for (const it of items) {
    const w = costWarning(it);
    if (w) out.push(w);
  }
  return out;
}
